// Catalog.ID Client — Registry-Request Mode Handlers
// ============================================================================
// Per-mode dispatch for registry-request envelopes received via the dashboard
// hub. The dispatcher in `client.ts` filters by envelope type and routes here.
//
// Inbound registry-request envelopes have already been signature-verified by
// the cld-id server's relay (`KeyGrantRelay
// .TryRejectUnverifiedRegistryRequestAsync`). Browser code does not re-verify;
// trust is anchored on the relay's verdict.
//
// Phase 5 Step C4 ships real orchestration:
//   - register-registry-key: consent → encryptClaim → POST /api/claims →
//     POST /api/claims/{uuid}/validate → outbound sign-challenge round-trip →
//     POST /api/claims/verify-registry-key/{token} → POST /api/claims/{id}/publish.
//   - key-revoked       : POST /api/claims/{claimUuid}/revoke.
//   - key-unpublished   : DELETE /api/publicclaims/{id} (id resolved from
//                         claim UUID).
//   - key-republished   : re-POST /api/claims/{claimUuid}/publish reusing the
//                         existing replacedExisting path on the cld-id server.
// ============================================================================

import type { Shell } from '@catalog-shell';
import type { RegistryRequest, RegistryResponse, RegistryResponsePhase } from './registry-types';
import { showRegistryKeyConsent } from './registry-key-consent-modal';
import { dispatchCeremonyRequest, type CeremonyDispatchDeps } from './ceremony-handlers';
import * as claimsApi from '../api/claims';
import * as publicClaimsApi from '../api/public-claims';
import * as accountApi from '../api/account';
import { encryptClaim, reEncryptClaimKey, reEncryptClaimKeyWithChain } from '../crypto/claims';
import { keys } from '../state/keys';
import { session } from '../state/session';
import * as aes from '../crypto/aes';
import { ApiError } from '../api/client';
import { ensureUnlocked } from './unlock-gate';

type ResponseBase = Pick<RegistryResponse, 'type' | 'to' | 'requestId'>;

// ── Audit C5: phase-tagged unavailable replies ──────────────────────────
//
// `before` builds a response for failures we KNOW didn't change peer state:
// validation gates, missing fields, lookups, 4xx responses where the server
// validated and refused without committing.
//
// `after` builds a response for failures where peer state MAY have changed:
// 5xx, network errors, or any failure that occurred after we crossed a
// state-mutating call. Registry flags affected keys for reconciliation rather
// than blindly retrying.
//
// `phaseFromError` classifies a thrown ApiError: 4xx → before-persist
// (server got the request and refused); 5xx or non-ApiError (network) →
// after-persist (uncertain).
function before(base: ResponseBase, reason: string): RegistryResponse {
    return { ...base, status: 'unavailable', reason, phase: 'before-persist' };
}
function after(base: ResponseBase, reason: string): RegistryResponse {
    return { ...base, status: 'unavailable', reason, phase: 'after-persist' };
}
function phaseFromError(err: unknown): RegistryResponsePhase {
    if (err instanceof ApiError && err.status >= 400 && err.status < 500) {
        return 'before-persist';
    }
    return 'after-persist';
}

export interface RegistryHandlerContext {
    shell: Shell;
    cid: string;
    /** Ask the hub to raise the CID tab for this request (M2c). Test seam;
     *  production uses the socket registered by `key-grant/client.ts`. */
    requestFocus?: (requestId: string) => void;
    /** Test seam; production uses the shared
     *  `unlock-gate.UNLOCK_WAIT_TIMEOUT_MS` (5 min). */
    unlockWaitTimeoutMs?: number;
    sendSignChallenge: (params: {
        claimUuid: string;
        fingerprint: string;
        challengeBase64: string;
    }) => Promise<RegistryResponse>;
}

export interface RegistryDispatchDeps {
    shell: Shell;
    /** Returns the active identity's cid, or null if no identity is active. */
    getActiveCid: () => string | null;
    /** Ask the hub to raise the CID tab for this request (M2c). Test seam;
     *  production uses the socket registered by `key-grant/client.ts`. */
    requestFocus?: (requestId: string) => void;
    /** Test seam; production uses the shared
     *  `unlock-gate.UNLOCK_WAIT_TIMEOUT_MS` (5 min). */
    unlockWaitTimeoutMs?: number;
    /** A8b ceremony deps — see {@link CeremonyDispatchDeps}. Optional so every
     *  pre-A8b construction site (and every existing spec) still compiles; a
     *  ceremony mode that needs a missing one answers `*-unavailable`. The
     *  authority key itself needs no dep: since cid-encrypted-storage M5 it is
     *  derived from the account's root and its generation state is a blob the
     *  handlers read for themselves. */
    listEnrollmentRecords?: CeremonyDispatchDeps['listEnrollmentRecords'];
    saveEnrollmentRecord?: CeremonyDispatchDeps['saveEnrollmentRecord'];
    /** Send an outbound registry-request {mode: "sign-challenge"} envelope to
     *  the registry module via the dashboard hub and await the matching
     *  registry-response. Used by register-registry-key publish orchestration. */
    sendSignChallenge: (params: {
        claimUuid: string;
        fingerprint: string;
        challengeBase64: string;
    }) => Promise<RegistryResponse>;
}

/**
 * Route a verified registry-request envelope to its mode-specific handler. The
 * caller has already received the envelope from the dashboard hub (where the
 * cld-id server's relay has signature-verified it) and is responsible only
 * for routing + a defence-in-depth `_senderServiceId` re-check.
 */
export async function dispatchRegistryRequest(
    request: RegistryRequest,
    deps: RegistryDispatchDeps,
): Promise<RegistryResponse> {
    const base: ResponseBase = {
        type: 'registry-response',
        to: request.requesterId,
        requestId: request.requestId,
    };

    if (!request._senderServiceId || request._senderServiceId !== request.requesterId) {
        return { ...base, status: 'unavailable', reason: 'sender-mismatch' };
    }

    // Audit C2: pin the requester to the well-known 'cpr' service id (the
    // registry module's hub id since the reg→cpr rename, 2026-06-05). The
    // sender-mismatch check above proves requesterId is what the hub
    // stamped, but a future hub-registered module under any other service id
    // could still pair sender + requesterId consistently and drive the
    // consent modal under its own identity. v1 has exactly one legitimate
    // requester for these flows.
    if (request.requesterId !== 'cpr') {
        return { ...base, status: 'unavailable', reason: 'unsupported-requester' };
    }

    const cid = deps.getActiveCid();
    if (!cid) return { ...base, status: 'unavailable', reason: 'no-active-identity' };

    const ctx: RegistryHandlerContext = {
        shell: deps.shell,
        cid,
        requestFocus: deps.requestFocus,
        unlockWaitTimeoutMs: deps.unlockWaitTimeoutMs,
        sendSignChallenge: deps.sendSignChallenge,
    };
    switch (request.mode) {
        // A8b: the keybag-hop ceremony modes. Routed here rather than into a
        // parallel dispatcher because they share this one's requester pin
        // (`cpr`) and its active-identity precondition exactly; a second
        // dispatcher would be a second copy of both gates.
        case 'ceremony-authority-status':
        case 'ceremony-generate-authority-key':
        case 'sign-ceremony-claims':
        case 'ceremony-commit':
            return await dispatchCeremonyRequest(request, {
                shell: deps.shell,
                getActiveCid: deps.getActiveCid,
                ...(deps.listEnrollmentRecords ? { listEnrollmentRecords: deps.listEnrollmentRecords } : {}),
                ...(deps.saveEnrollmentRecord ? { saveEnrollmentRecord: deps.saveEnrollmentRecord } : {}),
            });
        case 'register-registry-key':
            return await handleRegisterRegistryKey(request, base, ctx);
        case 'key-revoked':
            return await handleKeyRevoked(request, base, ctx);
        case 'key-unpublished':
            return await handleKeyUnpublished(request, base, ctx);
        case 'key-republished':
            return await handleKeyRepublished(request, base, ctx);
        default:
            return { ...base, status: 'unavailable', reason: 'unsupported-mode' };
    }
}

// ── register-registry-key ────────────────────────────────────────────────
//
// Eight-stage publish chain (Whitepaper §16.2a, §16.4):
//   1. Consent modal — user confirms publishing this key
//   2. encryptClaim under their attr_encrypt pubkey
//   3. POST /api/claims (claimType = registry_signing_key)
//   4. POST /api/claims/{uuid}/validate (validationType = registry_signing_key)
//      → returns { jobId, challenge }
//   5. Outbound sign-challenge round-trip via dashboard hub
//      → registry signs the challenge with the key's ML-DSA-65 private half
//   6. POST /api/claims/verify-registry-key/{token} (signature + publicKey)
//      → server certifies the claim
//   7. POST /api/claims/{uuid}/publish (operatorKeys array)
//      → claim enters operator review queue
//   8. Reply approved with { claimUuid }
//
// Failure between any post-debit step is surfaced with `unavailable` and a
// step-specific `reason` so the registry side does NOT persist
// `PublishedClaimUuid` (rolling back the local lifecycle change).
export async function handleRegisterRegistryKey(
    request: RegistryRequest,
    base: ResponseBase,
    ctx: RegistryHandlerContext,
): Promise<RegistryResponse> {
    // ── Pre-claim phase: every failure here is before-persist on cld-id
    // (the claim row hasn't been created yet, so cld-id state is unchanged).
    if (!request.fingerprint || !request.publicKey) {
        return before(base, 'missing-required-field');
    }

    // Step 0 — the universal unlock redirect (M2c, §10 item 5), AHEAD of the
    // consent dialog. The keybag has to be unlocked anyway (step 2 needs it so
    // `encryptClaim` succeeds and the key can later be re-wrapped for
    // operators), and a dialog drawn in a tab the member is not looking at is
    // invisible — the focus-request is what makes this tab visible, so it has
    // to come first. On an unlocked bag the gate is a no-op and the old
    // consent-first order stands.
    if (!await ensureUnlocked(request, ctx)) {
        return before(base, 'cid-locked');
    }

    // Step 1 — consent
    const decision = await showRegistryKeyConsent(ctx.shell, request, ctx.cid);
    if (decision !== 'approve') {
        // 'denied' is its own status; phase doesn't apply.
        return { ...base, status: 'denied', reason: 'user-denied' };
    }

    // The claim is created (step 3) with our attr_encrypt PUBLIC key, but the
    // validate step that follows needs the attr_encrypt PRIVATE key. Verify it
    // is actually present BEFORE the create-claim commit point: a partial
    // keybag (e.g. stale device-key copies left over from a crypto migration)
    // passes isLoaded() but would otherwise strand an orphan unvalidated claim
    // when validation can't find the key.
    if (!keys.hasKey('attr_encrypt')) {
        return before(base, 'attr-encrypt-locked');
    }

    let attrPublicKeyB64: string;
    try {
        const pubKeys = await accountApi.getOwnPublicKeys();
        const attrPub = pubKeys.find(k => k.keyId === 'attr_encrypt');
        if (!attrPub) {
            return before(base, 'no-attr-encrypt-key');
        }
        attrPublicKeyB64 = attrPub.publicKey;
    } catch (err) {
        // Read-only call; before-persist regardless of HTTP code.
        return before(base, 'fetch-pubkeys-failed:' + extractErrorCode(err));
    }

    // Step 2 — encryptClaim (local crypto only)
    let encrypted;
    try {
        encrypted = await encryptClaim(
            request.publicKey,
            aes.fromBase64(attrPublicKeyB64),
            request.label,
        );
    } catch (err) {
        return before(base, 'encrypt-failed:' + extractErrorCode(err));
    }

    // Step 3 — POST /api/claims (the create-claim commit point on cld-id).
    // From here on, EVERY failure is after-persist: the claim row exists on
    // cld-id and registry has no claim_uuid to reference it for cleanup. The
    // user must clean up manually via the cld-id UI.
    let claimUuid: string;
    try {
        const resp = await claimsApi.createClaim({
            claimType: 'registry_signing_key',
            encryptedValue: encrypted.encryptedValue,
            nonce: encrypted.nonce,
            encryptedKey: encrypted.encryptedKey,
            encryptedLabel: encrypted.encryptedLabel,
            labelNonce: encrypted.labelNonce,
        });
        claimUuid = resp.claimUuid;
    } catch (err) {
        // Phase from server response: 4xx → server refused, no commit;
        // 5xx/network → uncertain.
        return { ...base, status: 'unavailable',
            reason: 'create-claim-failed:' + extractErrorCode(err),
            phase: phaseFromError(err) };
    }

    // ── Post-claim phase: the claim row exists on cld-id. Every subsequent
    // failure leaves an orphan that the user must clean up.

    // Step 4 — POST /api/claims/{uuid}/validate (returns challenge)
    let challenge: string;
    try {
        const attrSecretKeyB64 = keys.getPrivateKey('attr_encrypt');
        if (!attrSecretKeyB64) {
            return after(base, 'attr-encrypt-locked');
        }
        const { publicKey: validatorKeyB64 } = await claimsApi.getValidatorKey();
        const validatorEncryptedKey = await reEncryptClaimKey(
            encrypted.encryptedKey,
            aes.fromBase64(attrSecretKeyB64),
            aes.fromBase64(validatorKeyB64),
        );
        const validateResp = await claimsApi.validateClaim(claimUuid, {
            validationType: 'registry_signing_key',
            encryptedKey: validatorEncryptedKey,
        });
        if (!validateResp.challenge) {
            return after(base, 'validate-no-challenge');
        }
        challenge = validateResp.challenge;
    } catch (err) {
        return after(base, 'validate-failed:' + extractErrorCode(err));
    }

    // Step 5 — outbound sign-challenge round-trip
    // The challenge is a UTF-8 string from the validator; we ship it through
    // registry for signing as the same UTF-8 byte sequence (base64-encoded for
    // the wire). registry signs the bytes returned by base64-decoding
    // `challengeBase64`; the verify endpoint at step 6 receives the original
    // challenge token (un-encoded) and re-derives the expected bytes the same
    // way. Mirrors how identity/src/client/pane-types/claim-detail-pane.ts
    // signs `new TextEncoder().encode(result.challenge)` server-side.
    const challengeBytes = new TextEncoder().encode(challenge);
    const challengeBase64 = aes.toBase64(challengeBytes);

    let signResponse: RegistryResponse;
    try {
        signResponse = await ctx.sendSignChallenge({
            claimUuid,
            fingerprint: request.fingerprint,
            challengeBase64,
        });
    } catch (err) {
        return after(base, 'sign-challenge-failed:' + extractErrorCode(err));
    }

    if (signResponse.status !== 'approved' || !signResponse.signature) {
        return after(base, 'sign-challenge-rejected:' + (signResponse.reason ?? signResponse.status));
    }

    // Step 6 — POST /api/claims/verify-registry-key/{token}
    try {
        const verifyResult = await claimsApi.verifyRegistryKey(
            challenge,
            signResponse.signature,
            request.publicKey,
        );
        if (!verifyResult.success) {
            // The validator rejected the ownership proof. Revert the claim out
            // of "pending" (best-effort) so it doesn't sit stuck — CancelValidation
            // marks the job failed and the claim unvalidated. Because the claim is
            // cleaned up, this is reported as before-persist (safe to retry).
            try { await claimsApi.cancelValidation(claimUuid); }
            catch { /* best-effort cleanup; don't mask the rejection */ }
            return before(base, 'verify-rejected');
        }
    } catch (err) {
        return after(base, 'verify-failed:' + extractErrorCode(err));
    }

    // Step 7 — POST /api/claims/{uuid}/publish
    try {
        const operators = await publicClaimsApi.listOperators();
        if (operators.length === 0) {
            return after(base, 'no-operators');
        }
        const attrSecretKeyB64 = keys.getPrivateKey('attr_encrypt');
        if (!attrSecretKeyB64) {
            return after(base, 'attr-encrypt-locked');
        }
        const operatorKeys: { operatorCid: string; encryptedKey: string }[] = [];
        for (const op of operators) {
            const reEncrypted = await reEncryptClaimKey(
                encrypted.encryptedKey,
                aes.fromBase64(attrSecretKeyB64),
                aes.fromBase64(op.publicKey),
            );
            operatorKeys.push({ operatorCid: op.cid, encryptedKey: reEncrypted });
        }
        await publicClaimsApi.publishClaim(claimUuid, { operatorKeys });

        // Auto-approve when the publishing session is itself an operator —
        // i.e. an owner/admin delegate of an org holding an active CPR binding,
        // or that org's own account session (operator-org-binding plan §2.7).
        // A personal session never qualifies, so the ordinary case now leaves
        // the claim pending for review in the operator pane; the server
        // re-derives the check anyway and a refusal here is non-fatal.
        if (session.isOperator()) {
            try {
                const allPublic = await publicClaimsApi.listPublicClaims();
                const pending = allPublic.find(pc => pc.claimUuid === claimUuid && pc.status === 'pending');
                if (pending) {
                    await publicClaimsApi.reviewPublicClaim(pending.publicClaimId, {
                        approved: true,
                        publicValue: request.publicKey,
                    });
                }
            } catch {
                // Self-review failed; not fatal — claim is still published as pending.
            }
        }

        ctx.shell.emit('data-changed', { type: 'claims' });
        ctx.shell.emit('data-changed', { type: 'publicclaims' });
    } catch (err) {
        return after(base, 'publish-failed:' + extractErrorCode(err));
    }

    // Step 8 — reply approved
    return {
        ...base,
        status: 'approved',
        claimUuid,
        publishedAt: new Date().toISOString(),
    };
}

// ── key-revoked ─────────────────────────────────────────────────────────
//
// registry has revoked the key locally and is propagating the event so the
// claim's `revokedAt` field is set on the catalog.id side. No keybag access
// needed — revoke is a server-side state change.
export async function handleKeyRevoked(
    request: RegistryRequest,
    base: ResponseBase,
    _ctx: RegistryHandlerContext,
): Promise<RegistryResponse> {
    if (!request.claimUuid) {
        return before(base, 'missing-claim-uuid');
    }
    try {
        await claimsApi.revokeClaim(request.claimUuid, {
            reason: request.reason ?? 'registry_revoke',
        });
    } catch (err) {
        if (err instanceof ApiError && err.status === 404) {
            // Server confirmed the claim does not exist — definitely no
            // state change.
            return before(base, 'claim-not-found');
        }
        // 4xx → server validated + refused → before-persist. 5xx / network
        // → uncertain → after-persist.
        return { ...base, status: 'unavailable',
            reason: 'revoke-failed:' + extractErrorCode(err),
            phase: phaseFromError(err) };
    }
    return { ...base, status: 'approved' };
}

// ── key-unpublished ─────────────────────────────────────────────────────
//
// The user (via registry) is hiding a published claim. We delete the publicclaim
// row, leaving the underlying encrypted claim intact. Reversible by
// key-republished. No keybag access needed — only the publication record is
// touched.
export async function handleKeyUnpublished(
    request: RegistryRequest,
    base: ResponseBase,
    _ctx: RegistryHandlerContext,
): Promise<RegistryResponse> {
    if (!request.claimUuid) {
        return before(base, 'missing-claim-uuid');
    }
    // List call — read-only, no state mutation possible. Failures here are
    // before-persist regardless of HTTP status.
    let target: { publicClaimId: number } | undefined;
    try {
        const all = await publicClaimsApi.listPublicClaims();
        target = all.find(pc => pc.claimUuid === request.claimUuid);
    } catch (err) {
        return before(base, 'unpublish-listing-failed:' + extractErrorCode(err));
    }
    if (!target) {
        return before(base, 'public-claim-not-found');
    }
    // Delete call — state-mutating. Phase depends on server response.
    try {
        await publicClaimsApi.deletePublicClaim(target.publicClaimId);
    } catch (err) {
        return { ...base, status: 'unavailable',
            reason: 'unpublish-failed:' + extractErrorCode(err),
            phase: phaseFromError(err) };
    }
    return { ...base, status: 'approved' };
}

// ── key-republished ─────────────────────────────────────────────────────
//
// Re-publish a claim that was previously unpublished. The claim row still
// exists on cld-id; we re-share its encrypted key with operators. The publish
// endpoint handles the existing-publication case via `replacedExisting`
// internally — we just call it.
export async function handleKeyRepublished(
    request: RegistryRequest,
    base: ResponseBase,
    ctx: RegistryHandlerContext,
): Promise<RegistryResponse> {
    if (!request.claimUuid) {
        return before(base, 'missing-claim-uuid');
    }

    if (!await ensureUnlocked(request, ctx)) {
        return before(base, 'cid-locked');
    }
    const attrSecretKeyB64 = keys.getPrivateKey('attr_encrypt');
    if (!attrSecretKeyB64) {
        return before(base, 'attr-encrypt-locked');
    }

    // Read calls — getClaim, listOperators. Failures here are read-only,
    // never mutate cld-id state, so before-persist regardless of HTTP code.
    let claim;
    try {
        claim = await claimsApi.getClaim(request.claimUuid);
    } catch (err) {
        if (err instanceof ApiError && err.status === 404) {
            return before(base, 'claim-not-found');
        }
        return before(base, 'getclaim-failed:' + extractErrorCode(err));
    }
    const selfKey = claim.keys.find(k => k.recipientType === 'self');
    if (!selfKey) {
        return before(base, 'no-self-key');
    }

    let operators;
    try {
        operators = await publicClaimsApi.listOperators();
    } catch (err) {
        return before(base, 'listoperators-failed:' + extractErrorCode(err));
    }
    if (operators.length === 0) {
        return before(base, 'no-operators');
    }

    const operatorKeys: { operatorCid: string; encryptedKey: string }[] = [];
    for (const op of operators) {
        let reEncrypted: string;
        try {
            if (selfKey.keyIndex > 0) {
                // Post-rotation: chain-walk to unwrap, then re-wrap for op
                const keyChain = keys.getKeyChain('attr_encrypt');
                reEncrypted = await reEncryptClaimKeyWithChain(
                    selfKey.encryptedKey, keyChain, selfKey.keyIndex, aes.fromBase64(op.publicKey));
            } else {
                reEncrypted = await reEncryptClaimKey(
                    selfKey.encryptedKey, aes.fromBase64(attrSecretKeyB64), aes.fromBase64(op.publicKey));
            }
        } catch (err) {
            // Local crypto failure — no peer mutation.
            return before(base, 'rewrap-failed:' + extractErrorCode(err));
        }
        operatorKeys.push({ operatorCid: op.cid, encryptedKey: reEncrypted });
    }

    // The state-mutating call. Phase from server response.
    try {
        await publicClaimsApi.publishClaim(request.claimUuid, { operatorKeys });
    } catch (err) {
        if (err instanceof ApiError && err.status === 404) {
            return before(base, 'claim-not-found');
        }
        return { ...base, status: 'unavailable',
            reason: 'republish-failed:' + extractErrorCode(err),
            phase: phaseFromError(err) };
    }

    // No self-review on republish: the public value (the registry key bytes)
    // would have to be decrypted first, which this path does not have cheaply.
    // The republished claim goes to the operator review queue like any other.
    return { ...base, status: 'approved' };
}

// ── helpers ─────────────────────────────────────────────────────────────

function extractErrorCode(err: unknown): string {
    if (err instanceof ApiError) {
        const body = err.body as Record<string, unknown> | string | undefined;
        if (body && typeof body === 'object' && typeof body['error'] === 'string') {
            return String(body['error']);
        }
        return `http-${err.status}`;
    }
    if (err instanceof Error) return err.message.slice(0, 80);
    return 'unknown';
}
